import { runAllocation, type AllocationInput } from './conflicts';
import type { AnalysisResults } from './types';

const TOGGLES = [
  'maintenance_blocking',
  'auto_recommendations',
  'network_saturation',
  'phased_array',
  'mspa_dsn',
  'data_rate_sla',
  'mission_sla_thresholds',
];
export type AllocationJob = Omit<AllocationInput, 'isToggleEnabled' | 'nowMs'> & {
  toggles: Record<string, boolean>;
  analysisTimeMs: number;
};
export type AllocationReply = { results: AnalysisResults } | { error: string };
export type AllocationWorker = Pick<Worker, 'postMessage' | 'terminate' | 'onmessage' | 'onerror'>;

/** One worker per large analysis; abort terminates superseded calculations. */
export function allocateOffThread(
  input: AllocationInput,
  signal?: AbortSignal,
  factory: () => AllocationWorker = () =>
    new Worker(new URL('./allocation.worker.ts', import.meta.url), { type: 'module' }),
): Promise<AnalysisResults> {
  if (signal?.aborted) return Promise.reject(new DOMException('Analysis superseded', 'AbortError'));
  if (input.missions.length * input.antennas.length < 10_000) return Promise.resolve(runAllocation(input));
  const { isToggleEnabled, nowMs, ...data } = input;
  const job: AllocationJob = {
    ...data,
    toggles: Object.fromEntries(TOGGLES.map((id) => [id, isToggleEnabled(id)])),
    analysisTimeMs: nowMs(),
  };
  return new Promise((resolve, reject) => {
    const worker = factory();
    const stop = () => {
      worker.terminate();
      signal?.removeEventListener('abort', abort);
    };
    const abort = () => {
      stop();
      reject(new DOMException('Analysis superseded', 'AbortError'));
    };
    worker.onmessage = (event: MessageEvent<AllocationReply>) => {
      stop();
      if ('error' in event.data) reject(new Error(event.data.error));
      else resolve(event.data.results);
    };
    worker.onerror = (event) => {
      stop();
      reject(new Error(event.message || 'Allocation worker failed'));
    };
    signal?.addEventListener('abort', abort, { once: true });
    try {
      worker.postMessage(job);
    } catch (err) {
      stop();
      reject(err);
    }
  });
}
